import { Card, SimpleGrid, Skeleton, Stack, Text } from "@mantine/core";
import { BarChart } from "@mantine/charts";
import { useMemo } from "react";
import { adminErrText, AdminApiError } from "../../../api.ts";
import { BotTimezoneNotice } from "../../../components/BotTimezoneNotice.tsx";
import { AnalyticsStatsFilters } from "./AnalyticsStatsFilters.tsx";
import { BAR_ITEMS_TOP_N } from "./stats-shared.ts";
import { ADS_GEO_SECTIONS, useAnalyticsStats } from "./useAnalyticsStats.ts";

type Props = {
  readonly botId: string;
};

export function AdsGeoTab({ botId }: Props) {
  const stats = useAnalyticsStats(botId, ADS_GEO_SECTIONS);
  const { data, isLoading, isError, error } = stats;

  const topAds = useMemo(() => {
    return (data?.topAds ?? []).slice(0, BAR_ITEMS_TOP_N).map((it) => ({
      adId: it.adId.length > 24 ? `${it.adId.slice(0, 24)}...` : it.adId,
      visits: it.visits,
      joined: it.joined,
      sent: it.sent,
      skipped: it.skipped ?? 0,
    }));
  }, [data?.topAds]);

  const countries = useMemo(() => {
    return (data?.countries ?? []).slice(0, BAR_ITEMS_TOP_N).map((it) => ({
      country: it.country,
      count: it.count,
    }));
  }, [data?.countries]);

  const adChartH = Math.min(420, Math.max(200, topAds.length * 28));
  const countryChartH = Math.min(420, Math.max(200, countries.length * 28));

  return (
    <Stack gap="md">
      {data !== undefined ? (
        <BotTimezoneNotice
          timezone={data.timezone}
          statsDay={data.statsDay}
          range={data.range}
          windowStartAt={data.windowStartAt}
          windowEndAt={data.windowEndAt}
        />
      ) : null}
      <AnalyticsStatsFilters stats={stats} />

      {isLoading ? (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Skeleton h={280} radius="md" />
          <Skeleton h={280} radius="md" />
        </SimpleGrid>
      ) : isError ? (
        <Text c="red" size="sm" ta="center" py="lg">
          {adminErrText((error as AdminApiError).code)}
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb={4}>
              Top 广告（归因访问）
            </Text>
            <Text size="xs" c="dimmed" mb="md">
              最多展示 {BAR_ITEMS_TOP_N} 条。含上报跳过（7 天用户去重等）。可用上方广告 ID
              精确筛选。
            </Text>
            {topAds.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                暂无广告数据
              </Text>
            ) : (
              <BarChart
                h={adChartH}
                data={topAds}
                dataKey="adId"
                orientation="vertical"
                withTooltip
                series={[
                  { name: "visits", label: "归因访问", color: "blue.6" },
                  { name: "joined", label: "入群", color: "teal.6" },
                  { name: "sent", label: "上报", color: "green.6" },
                  { name: "skipped", label: "跳过", color: "gray.6" },
                ]}
              />
            )}
          </Card>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb={4}>
              国家/地区分布
            </Text>
            <Text size="xs" c="dimmed" mb="md">
              最多展示 {BAR_ITEMS_TOP_N} 个地区。
            </Text>
            {countries.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                暂无地域数据
              </Text>
            ) : (
              <BarChart
                h={countryChartH}
                data={countries}
                dataKey="country"
                orientation="vertical"
                withTooltip
                series={[{ name: "count", label: "访问", color: "indigo.6" }]}
              />
            )}
          </Card>
        </SimpleGrid>
      )}
    </Stack>
  );
}
